import clsx from 'clsx';
import React from 'react';

import { Icon as GBIcon, type IconName, type IconProps } from '@gitbook/icons';
import { icons } from '@gitbook/icons/icons';
import { isEmojiCode } from '@gitbook/util-emoji-rendering';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { AtomButton, Button, type ButtonSize } from '../Button';
import { Emoji } from '../Emoji';
import { EmojiPicker } from '../EmojiPicker';
import type { OverlayTriggerState } from '../Overlay';
import { Popover, PopoverRoot, PopoverTrigger, usePopoverTriggerState } from '../Popover';
import { Tab, Tabs } from '../Tab';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon as renderSpineIcon } from '../util';
import { IconPicker } from './IconPicker';

/**
 * A button to pick an icon or emoji.
 *
 * It shows the currently selected value and opens a picker on press.
 * Note: this picker supports icons and emojis only (can be updated to support images).
 */
type IconPickerOption = 'icon' | 'emoji';

type IconPickerButtonProps = {
    /* The current icon or emoji. If undefined, the placeholder or a default value is shown. */
    value?: string;
    placeholder?: IconDefinition;
    /**
     * The callback triggered when an icon is picked.
     * - Single option: returns the picked value string
     * - Mutiple options: returns an object with the value type as key, e.g. { icon: 'book' } or { emoji: '😀' }
     */
    onPick:
        | ((value: string) => void)
        | ((value: string) => Promise<void>)
        | ((value: { [x: string]: string }) => void);
    onRemove?: () => void;
    accept?: IconPickerOption[];
    /** Size of the button. @default "medium" */
    size?: ButtonSize;
    /** Styles of the button. @default "secondary"
     * - use `custom` kind to integrate the button in UIs with specific colors (ie: hints)
     */
    kind?: 'secondary' | 'blank' | 'custom';
    /**
     * Styles applied on the button.
     * - kind `secondary` or `blank`: merged with the button kind styles.
     * - kind `custom`: replaces the button kind styles.
     */
    className?: string;
    /** Optional custom label for the button tooltip and aria-label. */
    label?: string;
    /** Optional custom renderer for GitBook icons. */
    renderIcon?: (props: IconProps) => React.ReactNode;
    /** Optional custom props for the button tooltip (override the label) */
    tooltip?: { label?: string; shortcut?: string };
} & React.ComponentPropsWithoutRef<typeof AtomButton>;

const defaultOption: IconPickerOption[] = ['icon', 'emoji'];

export function IconPickerButton(props: IconPickerButtonProps) {
    const { value, placeholder, onPick, onRemove, accept = defaultOption, ...buttonProps } = props;

    const popover = usePopoverTriggerState();

    const isSingleOption = accept.length === 1;

    return (
        <PopoverRoot {...popover.rootProps}>
            <PopoverTrigger>
                <IconButton
                    value={value}
                    placeholder={placeholder}
                    className={clsx(popover.isOpen && 'focus-ring', buttonProps.className)}
                    {...buttonProps}
                />
            </PopoverTrigger>
            <Popover className="p-0">
                {isSingleOption ? (
                    <SingleOptionIconPicker
                        value={value}
                        onPick={onPick as SingleOptionIconPickerProps['onPick']}
                        onRemove={onRemove}
                        iconType={accept[0]}
                        popover={popover}
                        renderIcon={props.renderIcon}
                    />
                ) : (
                    <MultipleOptionIconPicker
                        value={value}
                        onPick={onPick as MultipleOptionIconPickerProps['onPick']}
                        onRemove={onRemove}
                        accept={accept}
                        popover={popover}
                        renderIcon={props.renderIcon}
                    />
                )}
            </Popover>
        </PopoverRoot>
    );
}

/**
 * A picker with a header indicating the type of value to pick (icon or emoji).
 */
type SingleOptionIconPickerProps = Pick<IconPickerButtonProps, 'value' | 'onRemove'> & {
    iconType: IconPickerOption;
    onPick: ((value: string) => void) | ((value: string) => Promise<void>);
    popover: OverlayTriggerState;
    renderIcon?: IconPickerButtonProps['renderIcon'];
};
function SingleOptionIconPicker(props: SingleOptionIconPickerProps) {
    const { iconType, value, onRemove, onPick, popover, renderIcon } = props;
    const [query, setQuery] = React.useState('');

    const handlePick = useEventCallback((icon: string) => {
        popover.close();
        onPick(icon);
    });

    const handleRemove = useEventCallback(() => {
        popover.close();
        onRemove?.();
    });

    return (
        <div className="flex max-w-screen-xs flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-neutral-subtle bg-neutral-surface-subtle px-3 py-2">
                <span className="text-ui-standout">Pick an {iconType}</span>
                {value ? <Button onPress={handleRemove} label="Remove" kind="blank" /> : null}
            </div>
            {iconType === 'emoji' ? (
                <EmojiPicker query={query} onChangeQuery={setQuery} onPick={handlePick} />
            ) : null}
            {iconType === 'icon' ? (
                <IconPicker
                    query={query}
                    onChangeQuery={setQuery}
                    onPick={handlePick}
                    renderIcon={renderIcon}
                />
            ) : null}
        </div>
    );
}

/**
 * A picker with a tabbed interface to switch between options (icon and emoji).
 */
type MultipleOptionIconPickerProps = Pick<
    IconPickerButtonProps,
    'value' | 'onRemove' | 'accept' | 'renderIcon'
> & {
    onPick: (value: { [x: string]: string }) => void;
    popover: OverlayTriggerState;
};
function MultipleOptionIconPicker(props: MultipleOptionIconPickerProps) {
    const { value, onRemove, onPick, popover, accept = defaultOption, renderIcon } = props;

    const valueType = React.useMemo(() => resolveValueType(value), [value]);
    const [tab, setTab] = React.useState<(typeof accept)[number]>(valueType || 'icon');
    const [query, setQuery] = React.useState('');

    const handlePick = useEventCallback((value: string) => {
        popover.close();
        onPick({ [tab]: value });
    });

    const handleRemove = useEventCallback(() => {
        popover.close();
        onRemove?.();
    });

    return (
        <div className="mx-auto">
            <div className="flex flex-1 items-end gap-4 border-b px-3 pt-1">
                <Tabs className="flex-1">
                    <Tab active={tab === 'icon'} onPress={() => setTab('icon')} label="Icons" />
                    <Tab active={tab === 'emoji'} onPress={() => setTab('emoji')} label="Emojis" />
                </Tabs>
                {value ? (
                    <div className="flex py-[2px]">
                        <Button onPress={handleRemove} label="Remove" kind="blank" />
                    </div>
                ) : null}
            </div>
            {tab === 'emoji' ? (
                <EmojiPicker query={query} onChangeQuery={setQuery} onPick={handlePick} />
            ) : null}
            {tab === 'icon' ? (
                <IconPicker
                    query={query}
                    onChangeQuery={setQuery}
                    onPick={handlePick}
                    renderIcon={renderIcon}
                />
            ) : null}
        </div>
    );
}

/**
 * A button displaying the current value for an icon or emoji.
 */
const IconButton = function IconButton({
    ref,
    ...props
}: Omit<IconPickerButtonProps, 'onPick' | 'onRemove' | 'popover' | 'accept'> & {
    ref?: React.Ref<HTMLButtonElement>;
}) {
    const {
        value,
        placeholder,
        size = 'medium',
        tooltip,
        onFocus,
        onPointerEnter,
        onPressStart,
        renderIcon = (iconProps) => <GBIcon {...iconProps} />,
        ...buttonProps
    } = props;

    const valueType = React.useMemo(() => resolveValueType(value), [value]);
    const kindStyles = {
        secondary:
            'border border-control border-solid bg-base text-muted hover:text-base hover:bg-(--secondary-button-bg-hover)',
        blank: 'border-none bg-transparent text-muted hover:text-base hover:bg-(--neutral-a3) active:bg-(--neutral-a4)',
        custom: '',
    }[props.kind ?? 'secondary'];
    const iconSize = size === 'xlarge' ? 24 : size === 'xsmall' ? 14 : 16;
    const buttonLabel = props.label || 'Change icon';

    return (
        <Tooltip
            label={tooltip?.label || buttonLabel}
            shortcut={tooltip?.shortcut}
            disabled={!buttonLabel}
        >
            <AtomButton
                ref={ref}
                {...buttonProps}
                aria-label={buttonLabel}
                onFocus={(event) => {
                    onFocus?.(event);
                }}
                onPointerEnter={(event) => {
                    onPointerEnter?.(event);
                }}
                onPressStart={(event) => {
                    onPressStart?.(event);
                }}
                className={clsx(
                    'flex shrink-0 cursor-pointer items-center justify-center rounded-md transition focus:focus-ring',
                    {
                        xsmall: 'size-5',
                        small: 'size-6',
                        medium: 'size-8',
                        large: 'size-9',
                        xlarge: 'size-10',
                    }[size],
                    kindStyles,
                    props.className
                )}
            >
                {value && valueType === 'icon' ? (
                    renderIcon({ icon: value as IconName, size: iconSize })
                ) : value && valueType === 'emoji' ? (
                    <Emoji code={value} size={iconSize} />
                ) : placeholder ? (
                    renderSpineIcon(placeholder, { size: iconSize })
                ) : (
                    renderIcon({ icon: 'face-laugh', size: iconSize })
                )}
            </AtomButton>
        </Tooltip>
    );
};

/**
 * Determine if a value is an icon or emoji by checking against the available lists.
 */
function resolveValueType(value: string | undefined): 'icon' | 'emoji' | null {
    if (!value) {
        return null;
    }

    // Check if it's an icon by looking up in the icons list
    const isIcon = icons.some((icon) => icon.icon === value);
    if (isIcon) {
        return 'icon';
    }

    if (isEmojiCode(value)) {
        return 'emoji';
    }

    return null;
}
